import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { usageApi } from '../../api/usage';
import { promoCodesApi } from '../../api/promocodes';
import Button from '../ui/Button';
import {
  X,
  QrCode,
  Barcode,
  Check,
  AlertCircle,
  User,
  MessageSquare,
  ShoppingBag,
  Zap,
  Store,
  Percent,
  Copy,
  Tag,
  Heart
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const DiscountUsageModal = ({ discount, onClose, onSuccess }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [savedAmount, setSavedAmount] = useState('');
  const [notes, setNotes] = useState('');

  const { data: usageCheckData } = useQuery({
    queryKey: ['usage-check', user?.id, discount?.id],
    queryFn: () => usageApi.checkUsage(user.id, discount.id).then(res => res.data),
    enabled: !!user?.id && !!discount?.id,
  });

  const hasUsedDiscount = usageCheckData?.used === true;

  const { data: promoCode, isLoading: isLoadingPromo, error: promoError } = useQuery({
    queryKey: ['promoCode', user?.id, discount?.id],
    queryFn: () => promoCodesApi.getOrAssignPromoCode(user.id, discount.id).then(res => res.data),
    enabled: !!user?.id && !!discount?.id && discount.promoCodeMode !== 'NONE',
  });

  const recordUsageMutation = useMutation({
    mutationFn: ({ userId, discountId, savedAmount, notes, promoCodeId }) =>
      usageApi.recordUsage(userId, discountId, savedAmount, notes, promoCodeId),
    onSuccess: () => {
      toast.success('Використання знижки успішно зареєстровано!');
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (error) => {
      console.error('Error recording usage:', error);
      toast.error(error.response?.data?.message || 'Помилка при реєстрації використання знижки');
    },
  });

  const handleUseDiscount = () => {
    if (!user?.id) {
      toast.error('Помилка: користувач не знайдений');
      return;
    }
    if (!discount?.id) {
      toast.error('Помилка: знижка не знайдена');
      return;
    }
    recordUsageMutation.mutate({
      userId: user.id,
      discountId: discount.id,
      savedAmount: 0,
      notes: null,
      promoCodeId: promoCode?.id
    });
  };

  const handleSubmitReview = (e) => {
    e.preventDefault();
    const amountValue = savedAmount ? parseFloat(savedAmount) : 0;
    recordUsageMutation.mutate({
      userId: user.id,
      discountId: discount.id,
      savedAmount: amountValue,
      notes: notes.trim() || null,
      promoCodeId: promoCode?.id
    });
  };

  const handleCopyPromoCode = () => {
    if (promoCode?.code) {
      navigator.clipboard.writeText(promoCode.code);
      toast.success('Промокод скопійовано!');
    }
  };

  const isSubmitting = recordUsageMutation.isPending;
  if (!discount) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-hidden">
      <div className="relative w-full max-w-lg sm:max-w-xl md:max-w-2xl my-auto max-h-[95vh] flex flex-col">

        {/* Анімований Zap у фоні - тільки на desktop */}
        <div className="absolute inset-0 items-center justify-center z-0 pointer-events-none hidden sm:flex">
          <div className="spinning-element">
            <Zap size={96} className="text-yellow-400 drop-shadow-[0_0_20px_rgba(250,204,21,0.8)]" />
          </div>
        </div>

        {/* Анімована світлова рамка */}
        <div className="absolute -inset-1 rounded-2xl border-2 border-transparent animated-border z-15" />

        {/* Рухома стрічка навколо вікна */}
        <div className="absolute -inset-1 rounded-2xl overflow-hidden pointer-events-none z-10">
          {/* Верхня стрічка */}
          <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-r from-primary-600 to-primary-500 flex items-center overflow-hidden">
            <div className="marquee-content whitespace-nowrap text-white font-bold text-sm">
              ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС
            </div>
          </div>
          {/* Нижня стрічка */}
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-r from-primary-500 to-primary-600 flex items-center overflow-hidden">
            <div className="marquee-content-reverse whitespace-nowrap text-white font-bold text-sm">
              ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС
            </div>
          </div>
          {/* Ліва стрічка */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-b from-primary-600 to-primary-500 flex items-center overflow-hidden">
            <div className="marquee-content-vertical whitespace-nowrap text-white font-bold text-sm transform -rotate-90 origin-center">
              ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС
            </div>
          </div>
          {/* Права стрічка */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-b from-primary-500 to-primary-600 flex items-center overflow-hidden">
            <div className="marquee-content-vertical-reverse whitespace-nowrap text-white font-bold text-sm transform -rotate-90 origin-center">
              ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС ПРОФБОНУС
            </div>
          </div>
        </div>

        <div className="relative bg-white rounded-lg z-20 shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200">
            <h3 className="text-base sm:text-xl font-bold text-gray-800 flex items-center">
              <ShoppingBag size={18} className="mr-2 text-primary-600 sm:block hidden" />
              {step === 1 ? 'Використання знижки' : 'Ваш відгук'}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
              disabled={isSubmitting}
            >
              <X size={18} />
            </button>
          </div>

          {/* Біжучий рядок всередині вікна */}
          <div className="bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 overflow-hidden py-2">
            <div className="marquee-content-inner whitespace-nowrap text-white font-bold text-sm sm:text-base">
              ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡ ПРОФБОНУС ⚡
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
          {hasUsedDiscount ? (
            <div className="p-6 sm:p-10 text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Heart size={40} className="text-green-600" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-green-800">
                Дякуємо, що скористалися знижкою!
              </h3>
              <p className="text-gray-600">
                Ви вже скористалися знижкою <strong>{discount.title}</strong> від {discount.partnerName}.
              </p>
              <div className="pt-4">
                <Button variant="outline" onClick={onClose} className="px-8">
                  Закрити
                </Button>
              </div>
            </div>
          ) : step === 1 ? (
            <div className="p-3 sm:p-6 lg:p-8 space-y-3 sm:space-y-6">
              {/* Блок партнера - компактний */}
              <div className="bg-primary-50 p-3 sm:p-6 rounded-lg border border-primary-200 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-200/30 via-primary-100/20 to-transparent animate-[pulse_3s_infinite]" />
                <div className="relative z-10 flex items-center space-x-3">
                  <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-full overflow-hidden border-2 border-white shadow-md flex-shrink-0">
                    {discount.partnerLogoUrl ? (
                      <img
                        src={discount.partnerLogoUrl}
                        alt={discount.partnerName}
                        className="w-full h-full object-cover"
                        onError={(e) => e.currentTarget.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-full h-full bg-primary-100 flex items-center justify-center">
                        <Store size={16} className="text-primary-600 sm:size-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm sm:text-lg text-gray-800 truncate">{discount.partnerName}</h4>
                    <p className="text-primary-700 font-medium text-xs sm:text-base truncate">{discount.title}</p>
                    <div className="flex items-center mt-1">
                      {discount.type === 'PERCENTAGE' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Percent size={10} className="mr-1" />
                          {discount.percentageValue}%
                        </span>
                      )}
                      {discount.type === 'FIXED' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {discount.fixedValue} грн
                        </span>
                      )}
                      {discount.type === 'SPECIAL_OFFER' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <Tag size={10} className="mr-1" />
                          Спеціальна пропозиція
                        </span>
                      )}
                      {(discount.priceRangeMin || discount.priceRangeMax) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 ml-1">
                          {discount.priceRangeMin}–{discount.priceRangeMax} грн
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Профіль користувача - компактний */}
              <div className="p-3 sm:p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                <div className="flex items-center mb-3 space-x-2">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                    {user?.profilePictureUrl ? (
                      <img src={user.profilePictureUrl} alt={user.fullName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-primary-100 flex items-center justify-center">
                        <User size={16} className="text-primary-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="font-bold text-xs sm:text-sm truncate">{user?.fullName}</h5>
                    <p className="text-xs text-gray-600 truncate">{user?.departmentName}</p>
                  </div>
                </div>

                {/* QR код - оптимізований для мобільних */}
                {discount.qrCodeData && (
                  <div className="text-center mb-3 bg-gradient-to-br from-primary-50 to-white p-3 sm:p-4 rounded-lg border-2 border-primary-200">
                    <div className="flex items-center justify-center mb-2">
                      <QrCode size={20} className="text-primary-600 animate-bounce mr-1" />
                      <span className="text-sm sm:text-lg font-semibold text-gray-800">QR код</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-lg inline-block shadow-md">
                      <img
                        src={discount.qrCodeData}
                        alt="QR"
                        className="mx-auto w-40 h-40 sm:w-56 sm:h-56 md:w-64 md:h-64"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">Покажіть партнеру</p>
                  </div>
                )}

                {/* Штрих-код - оптимізований */}
                {discount.barcodeData && (
                  <div className="text-center bg-gradient-to-br from-blue-50 to-white p-3 sm:p-4 rounded-lg border-2 border-blue-200">
                    <div className="flex items-center justify-center mb-2">
                      <Barcode size={20} className="text-blue-600 animate-pulse mr-1" />
                      <span className="text-sm sm:text-lg font-semibold text-gray-800">Штрих-код</span>
                    </div>
                    <div className="bg-white p-2 sm:p-3 rounded-lg shadow-md">
                      <img
                        src={discount.barcodeData}
                        alt="Barcode"
                        className="mx-auto max-w-full h-16 sm:h-24 md:h-32"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">Покажіть партнеру</p>
                  </div>
                )}

                {/* Промокод - якщо є */}
                {discount.promoCodeMode !== 'NONE' && (
                  <div className="text-center bg-gradient-to-br from-purple-50 to-white p-3 sm:p-4 rounded-lg border-2 border-purple-200 mt-3">
                    <div className="flex items-center justify-center mb-2">
                      <Tag size={20} className="text-purple-600 animate-bounce mr-1" />
                      <span className="text-sm sm:text-lg font-semibold text-gray-800">Промокод</span>
                    </div>
                    {isLoadingPromo ? (
                      <div className="bg-white p-3 rounded-lg shadow-md">
                        <div className="animate-pulse flex items-center justify-center">
                          <div className="h-8 w-32 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    ) : promoCode ? (
                      <div className="bg-white p-3 rounded-lg shadow-md">
                        <div className="flex items-center justify-center gap-2">
                          <code className="text-lg sm:text-2xl font-bold text-purple-600 tracking-wider">
                            {promoCode.code}
                          </code>
                          <button
                            onClick={handleCopyPromoCode}
                            className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors"
                            title="Копіювати промокод"
                          >
                            <Copy size={18} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white p-3 rounded-lg shadow-md">
                        <p className="text-sm text-red-600">Промокоди закінчились</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center pt-3 border-t border-gray-200">
                <Button
                  variant="primary"
                  onClick={handleUseDiscount}
                  loading={isSubmitting}
                  icon={<ShoppingBag size={16} />}
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base font-semibold"
                >
                  Використати знижку
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  icon={<MessageSquare size={16} />}
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base border-2"
                >
                  Залишити відгук
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitReview} className="p-4 sm:p-6">
              <div className="flex items-center mb-4 p-3 bg-amber-50 rounded-lg text-amber-700 border border-amber-200 relative">
                <AlertCircle size={18} className="mr-2 text-amber-500" />
                <p className="text-sm">
                  Будь ласка, підтвердіть використання знижки та вкажіть суму заощаджень.
                </p>
              </div>

              <input
                type="number"
                min="0"
                step="0.01"
                value={savedAmount}
                onChange={(e) => setSavedAmount(e.target.value)}
                className="block w-full p-2 mb-4 border rounded-md"
                placeholder="Сума заощаджень"
              />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="block w-full p-2 border rounded-md mb-4"
                rows="3"
                placeholder="Ваш відгук (опціонально)"
              ></textarea>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Назад
                </Button>
                <Button type="submit" variant="primary" loading={isSubmitting} icon={<Check size={16} />}>
                  Надіслати
                </Button>
              </div>
            </form>
          )}
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg) scale(1); }
            50% { transform: rotate(180deg) scale(1.3); }
            100% { transform: rotate(360deg) scale(1); }
          }
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes marquee {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          @keyframes marqueeReverse {
            0% { transform: translateX(-50%); }
            100% { transform: translateX(0%); }
          }
          @keyframes marqueeVertical {
            0% { transform: translateY(0%) rotate(-90deg); }
            100% { transform: translateY(-50%) rotate(-90deg); }
          }
          @keyframes marqueeVerticalReverse {
            0% { transform: translateY(-50%) rotate(-90deg); }
            100% { transform: translateY(0%) rotate(-90deg); }
          }
          .spinning-element {
            animation: spin 3s linear infinite;
          }
          .animated-border {
            background: linear-gradient(270deg, #facc15, #f59e0b, #facc15);
            background-size: 600% 600%;
            animation: gradientMove 4s ease infinite;
            border-radius: 1rem;
          }
          .marquee-content {
            animation: marquee 15s linear infinite;
            display: inline-block;
            padding-left: 100%;
          }
          .marquee-content-reverse {
            animation: marqueeReverse 15s linear infinite;
            display: inline-block;
            padding-left: 100%;
          }
          .marquee-content-vertical {
            animation: marqueeVertical 20s linear infinite;
            display: inline-block;
            width: max-content;
          }
          .marquee-content-vertical-reverse {
            animation: marqueeVerticalReverse 20s linear infinite;
            display: inline-block;
            width: max-content;
          }
          .marquee-content-inner {
            animation: marquee 20s linear infinite;
            display: inline-block;
            padding-left: 100%;
          }
        `
      }} />
    </div>
  );
};

export default DiscountUsageModal;
